// services/misApi.js
import axios from 'axios';

// Base API URL - adjust this to match your backend URL
const API_URL = 'https://accountss-3.onrender.com/api';

// Create axios instance with default headers
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token to every request
axiosInstance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        // Check if token already includes "Bearer"
        if (token.startsWith('Bearer ')) {
          config.headers['Authorization'] = token;
        } else {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

// Response interceptor to handle common errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized errors (expired token, etc.)
    if (error.response && error.response.status === 401) {
      // Redirect to login or clear user session
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// MIS API Service
export const misApi = {
  /**
   * Upload transactions Excel file
   * @param {File} file - Excel file to upload
   * @param {Function} progressCallback - Optional callback for upload progress
   * @returns {Promise} - Promise with response data
   */
  uploadTransactions: async (file, progressCallback = null) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };
      
      // Add progress tracking if callback provided
      if (progressCallback) {
        config.onUploadProgress = (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          progressCallback(percentCompleted);
        };
      }
      
      const response = await axiosInstance.post('/transactions/upload', formData, config);
      return response;
    } catch (error) {
      console.error('Error uploading transactions:', error);
      throw error;
    }
  },




  // P&L TREND REPORTS... API CONNECTIONS ... 

  // Add these three methods to your misApi object:

/**
 * Get profit and loss trend report data for a specific year
 * @param {string} year - Year to fetch trend data for (e.g., '2025')
 * @returns {Promise} - Promise with response data
 */
getPLTrendReport: async (year) => {
  try {
    const response = await axiosInstance.get('/transactions/pl-trend', {
      params: { year }
    });
    return response;
  } catch (error) {
    console.error('Error fetching P&L trend data:', error);
    throw error;
  }
},

/**
 * Update profit and loss trend data
 * @param {Object} data - Data to update (includes category_id, month, value)
 * @returns {Promise} - Promise with response data
 */
updatePLTrendData: async (data) => {
  try {
    const response = await axiosInstance.put('/transactions/pl-trend/update', data);
    return response;
  } catch (error) {
    console.error('Error updating P&L trend data:', error);
    throw error;
  }
},

/**
 * Export profit and loss trend data to Excel
 * @param {string} year - Year to export trend data for (e.g., '2025')
 * @returns {Promise} - Promise with blob data for Excel file
 */
exportPLTrend: async (year) => {
  try {
    const response = await axiosInstance.get('/transactions/pl-trend/export', {
      params: { year },
      responseType: 'blob'
    });
    return response;
  } catch (error) {
    console.error('Error exporting P&L trend data:', error);
    throw error;
  }
},







  /**
 * Populate last year data for a specific month
 * @param {string} month - Month to populate last year data for (format: 'Jan 2025')
 * @returns {Promise} - Promise with response data
 */
populateLastYearData: async (month) => {
  try {
    const response = await axiosInstance.post('/transactions/populate-last-year', { month });
    return response;
  } catch (error) {
    console.error('Error populating last year data:', error);
    throw error;
  }
},
  
  /**
   * Manually recalculate profit and loss data for a month
   * @param {string} month - Month to recalculate data for (format: 'Jan 2025')
   * @returns {Promise} - Promise with response data
   */
  recalculateProfitLoss: async (month) => {
    try {
      const response = await axiosInstance.post('/transactions/recalculate', null, {
        params: { month }
      });
      return response;
    } catch (error) {
      console.error('Error recalculating profit and loss data:', error);
      throw error;
    }
  },

  
  
  /**
   * Get profit and loss report data
   * @param {string} month - Month to fetch data for (format: 'Jan 2025')
   * @returns {Promise} - Promise with response data
   */
  getProfitLossData: async (month = 'Jan 2025') => {
    try {
      const response = await axiosInstance.get('/profit-loss/profit-loss', {
        params: { month }
      });
      return response;
    } catch (error) {
      console.error('Error fetching profit and loss data:', error);
      throw error;
    }
  },

  /**
 * Get profit and loss report data with last year comparisons
 * @param {string} month - Month to fetch data for (format: 'Jan 2025')
 * @returns {Promise} - Promise with response data
 */
getProfitLossReport: async (month = 'Jan 2025') => {
  try {
    const response = await axiosInstance.get('/transactions/profit-loss-report', {
      params: { month }
    });
    return response;
  } catch (error) {
    console.error('Error fetching profit and loss report:', error);
    throw error;
  }
},
  
  /**
   * Get detailed data for a specific category
   * @param {string} category - Category name (e.g., 'REVENUE')
   * @param {string} month - Month to fetch data for (format: 'Jan 2025')
   * @returns {Promise} - Promise with response data
   */
  getCategoryData: async (category, month = 'Jan 2025') => {
    try {
      const response = await axiosInstance.get('/profit-loss/category', {
        params: { category, month }
      });
      return response;
    } catch (error) {
      console.error('Error fetching category data:', error);
      throw error;
    }
  },
  
  /**
   * Initialize profit and loss categories (if not already created)
   * @returns {Promise} - Promise with response data
   */
  initializeCategories: async () => {
    try {
      const response = await axiosInstance.post('/profit-loss/initialize');
      return response;
    } catch (error) {
      console.error('Error initializing categories:', error);
      throw error;
    }
  },

  /**
   * Initialize extended profit and loss categories with all sections
   * @param {boolean} reinitialize - Whether to clear existing categories first
   * @returns {Promise} - Promise with response data
   */
  initializeExtendedCategories: async (reinitialize = false) => {
    try {
      const response = await axiosInstance.post('/profit-loss/initialize-extended', null, {
        params: { reinitialize }
      });
      return response;
    } catch (error) {
      console.error('Error initializing extended categories:', error);
      throw error;
    }
  },

  /**
   * Get account details for a specific category
   * @param {number} categoryId - Category ID to fetch accounts for
   * @param {string} month - Month to fetch data for (format: 'Jan 2025')
   * @returns {Promise} - Promise with response data
   */
  getCategoryAccountDetails: async (categoryId, month) => {
    try {
      console.log("API call with categoryId:", categoryId, "Type:", typeof categoryId);
      
      // Make sure categoryId is a valid number
      const categoryIdParam = Number(categoryId);
      if (isNaN(categoryIdParam)) {
        console.error("Invalid category ID:", categoryId);
        throw new Error("Invalid category ID");
      }
      
      // Get category name first to determine if it's the "Other Income" category
      const categoryResponse = await axiosInstance.get(`/transactions/categories/${categoryIdParam}`);
      const categoryName = categoryResponse.data?.data?.name;

      console.log("Category name:", categoryName);

      // Parse month to get date range
      const dateRange = misApi.getMonthDateRange(month);
      console.log("Date range:", dateRange);

      // Now fetch the account details using a different variable name
      const response = await axiosInstance.get('/transactions/category-accounts', {
        params: {
          category_id: categoryIdParam,
          start_date: dateRange[0],
          end_date: dateRange[1]
        }
      });
      
      return response;
    } catch (error) {
      console.error('Error request details:', error.config);
      console.error('Error fetching category account details:', error);
      throw error;
    }
  },

  /**
   * Get category by ID
   * @param {number} categoryId - Category ID to fetch
   * @returns {Promise} - Promise with response data
   */
  getCategoryById: async (categoryId) => {
    try {
      const response = await axiosInstance.get(`/transactions/categories/${categoryId}`);
      return response;
    } catch (error) {
      console.error('Error fetching category by ID:', error);
      throw error;
    }
  },

  /**
   * Get monthly data for a specific category
   * @param {number} categoryId - Category ID to fetch data for
   * @param {string} month - Month to fetch data for (format: 'Jan 2025')
   * @returns {Promise} - Promise with response data
   */
  getCategoryMonthlyData: async (categoryId, month) => {
    try {
      const response = await axiosInstance.get('/profit-loss/category-data', {
        params: {
          category_id: categoryId,
          month
        }
      });
      return response;
    } catch (error) {
      console.error('Error fetching category monthly data:', error);
      throw error;
    }
  },

  /**
   * Get transactions for a specific account
   * @param {string} accountNumber - Account number to fetch transactions for
   * @param {string} startDate - Start date (format: 'YYYY-MM-DD')
   * @param {string} endDate - End date (format: 'YYYY-MM-DD')
   * @returns {Promise} - Promise with response data
   */
  getAccountTransactions: async (accountNumber, startDate, endDate) => {
    try {
      const response = await axiosInstance.get('/transactions/account-details', {
        params: {
          account: accountNumber,
          start_date: startDate,
          end_date: endDate
        }
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching account transactions:', error);
      throw error;
    }
  },

  /**
   * Check for date issues in transactions
   * @param {string} accounts - Comma-separated list of account numbers to check
   * @param {string} createdAfter - Check transactions created after this date
   * @returns {Promise} - Promise with response data about date issues
   */
  checkDateIssues: async (accounts, createdAfter) => {
    try {
      const response = await axiosInstance.get('/transactions/check-date-issues', {
        params: {
          accounts,
          created_after: createdAfter
        }
      });
      return response;
    } catch (error) {
      console.error('Error checking date issues:', error);
      throw error;
    }
  },

  /**
   * Fix date issues in transactions
   * @param {Array} accounts - Array of account numbers to fix dates for
   * @returns {Promise} - Promise with response data about fixed transactions
   */
  fixDateIssues: async (accounts) => {
    try {
      const response = await axiosInstance.post('/transactions/fix-dates', { accounts });
      return response;
    } catch (error) {
      console.error('Error fixing date issues:', error);
      throw error;
    }
  },

  /**
   * Helper function to get date range for a month
   * @param {string} monthStr - Month string (format: 'Jan 2025')
   * @returns {Array} - Array with start and end dates [startDate, endDate]
   */
  getMonthDateRange: (monthStr) => {
    // Parse month string (e.g., "Dec 2023")
    const [monthName, yearStr] = monthStr.split(' ');
    const year = parseInt(yearStr);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = monthNames.indexOf(monthName);
    
    // Create start and end dates
    const startDate = new Date(year, monthIndex, 1);
    const endDate = new Date(year, monthIndex + 1, 0); // Last day of month
    
    // Format dates as YYYY-MM-DD
    const formatDate = (date) => {
      return date.toISOString().split('T')[0];
    };
    
    return [formatDate(startDate), formatDate(endDate)];
  },
  
  /**
   * Get financial indicators for a month
   * @param {string} month - Month to calculate indicators for (format: 'Jan 2025')
   * @returns {Promise} - Promise with response data
   */
  getFinancialIndicators: async (month = 'Jan 2025') => {
    try {
      const response = await axiosInstance.get('/profit-loss/indicators', {
        params: { month }
      });
      return response;
    } catch (error) {
      console.error('Error fetching financial indicators:', error);
      throw error;
    }
  },
  
  /**
   * Compare profit and loss data between two months
   * @param {string} month1 - First month for comparison (format: 'Jan 2025')
   * @param {string} month2 - Second month for comparison (format: 'Jan 2025')
   * @returns {Promise} - Promise with response data
   */
  compareMonths: async (month1, month2) => {
    try {
      const response = await axiosInstance.get('/profit-loss/compare', {
        params: { month1, month2 }
      });
      return response;
    } catch (error) {
      console.error('Error comparing months:', error);
      throw error;
    }
  },

  addCategory: async (categoryData) => {
    try {
      const response = await axiosInstance.post('/profit-loss/categories', categoryData);
      return response;
    } catch (error) {
      console.error('Error adding category:', error);
      throw error;
    }
  },
  
  updateCategory: async (id, categoryData) => {
    try {
      const response = await axiosInstance.put(`/profit-loss/categories/${id}`, categoryData);
      return response;
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  },
  
  deleteCategory: async (id) => {
    try {
      const response = await axiosInstance.delete(`/profit-loss/categories/${id}`);
      return response;
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  },
  
  updateProfitLossData: async (data) => {
    try {
      const response = await axiosInstance.put('/transactions/update-profit-loss', data);
      return response;
    } catch (error) {
      console.error('Error updating profit and loss data:', error);
      throw error;
    }
  },

  /**
   * Delete a transaction and recalculate profit/loss data
   * @param {number} transactionId - ID of the transaction to delete
   * @returns {Promise} - Promise with response data
   */
  deleteTransaction: async (transactionId) => {
    try {
      const response = await axiosInstance.delete(`/transactions/${transactionId}`);
      return response;
    } catch (error) {
      console.error('Error deleting transaction:', error);
      throw error;
    }
  },
  
  /**
   * Export profit and loss data to Excel
   * @param {string} month - Month to export data for (format: 'Jan 2025')
   * @returns {Promise} - Promise with blob data for Excel file
   */
  exportProfitLoss: async (month = 'Jan 2025') => {
    try {
      const response = await axiosInstance.get('/profit-loss/export', {
        params: { month },
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      console.error('Error exporting profit and loss data:', error);
      throw error;
    }
  },
  
  /**
   * Get available months with transaction data
   * @returns {Promise} - Promise with array of available months
   */
  getAvailableMonths: async () => {
    try {
      const response = await axiosInstance.get('/transactions/months');
      return response;
    } catch (error) {
      console.error('Error fetching available months:', error);
      throw error;
    }
  }
};

export default misApi;